import React, { useState, useRef, useEffect } from 'react';
import { calculateCheckDigit, normalizeGTINForStorage } from '../libs/gtinUtils.js';

/**
 * Reusable GTIN/UPC input component with comprehensive validation
 * @param {Object} props
 * @param {string} props.value - Current GTIN value
 * @param {Function} props.onChange - Callback when value changes
 * @param {Function} props.onBlur - Callback when field loses focus
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.excludeProductId - Product ID to exclude from conflict checking
 * @param {string} props.excludeCasepackId - Casepack ID to exclude from conflict checking
 * @param {Array} props.existingProducts - Array of existing products for conflict checking
 * @param {boolean} props.isBulkEditing - Whether we're in bulk edit mode
 * @param {Object} props.bulkEditData - Bulk edit data for conflict checking
 * @param {string} props.originalGtin - Original GTIN value for change detection
 * @param {string} props.errorPosition - Position for error message ('left', 'right', 'bottom')
 * @param {number} props.maxWidth - Maximum width for error message
 * @param {number} props.maxLength - Maximum length for GTIN (12 for UPC, 13 for EAN, 14 for GTIN)
 */
        const GTINInput = ({
          value = '',
          onChange,
          onBlur,
          placeholder = null,
          className = '',
          disabled = false,
          excludeProductId = null,
          excludeCasepackId = null,
          existingProducts = [],
          isBulkEditing = false,
          bulkEditData = {},
          originalGtin = null,
          errorPosition = 'left',
          maxWidth = 256,
          maxLength = 12,
          borderless = false,
          textAlign = 'left',
          onValidationChange = null,
          alwaysShowPadding = false,
          autoComplete = 'off'
        }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [cleanedValue, setCleanedValue] = useState(value);
  const inputRef = useRef(null);

            // Update cleaned value when external value changes
          useEffect(() => {
            setCleanedValue(value);
          }, [value]);

          // Notify parent of validation state changes
          useEffect(() => {
            if (onValidationChange) {
              const validation = validateGTIN(value);
              const validationState = {
                hasError: !validation.isValid || validation.hasConflict,
                errorMessage: validation.errorMessage || (validation.hasConflict ? `Already in use by ${validation.conflictingProduct}` : null)
              };
              onValidationChange(validationState);
            }
          }, [value]); // Only depend on value to avoid infinite loops

  /**
   * Clean GTIN value by removing leading zeros but keeping at least 12 digits
   */
  const cleanGTINValue = (value) => {
    if (!value || value.length === 0) return value;
    
    // If alwaysShowPadding is true, don't remove leading zeros
    if (alwaysShowPadding) {
      return value;
    }
    
    // Remove leading zeros but keep at least 12 digits for UPCs
    const withoutLeadingZeros = value.replace(/^0+/, '');
    const cleanedValue = withoutLeadingZeros.length < 12 ? value.slice(-12) : withoutLeadingZeros;
    return cleanedValue;
  };

  /**
   * Validate GTIN format and check digit
   */
  const validateGTINFormat = (gtin) => {
    if (!gtin || gtin === '') return { isValid: true, errorMessage: null };
    
    // For GTIN-14, allow 12-digit UPCs (they'll be converted to 14-digit GTINs)
    if (maxLength === 14 && gtin.length === 12) {
      // Validate as UPC - will be converted to GTIN-14 with leading zeros
      const paddedGtin = gtin.padStart(14, '0');
      const calculatedCheckDigit = calculateCheckDigit(paddedGtin.slice(0, 13));
      const providedCheckDigit = parseInt(gtin.slice(-1));
      
      if (calculatedCheckDigit !== providedCheckDigit) {
        return { 
          isValid: false, 
          errorMessage: `Invalid check digit. Expected ${calculatedCheckDigit}, got ${providedCheckDigit}.` 
        };
      }
      return { isValid: true, errorMessage: null };
    }

    // For consumer identifiers, allow either UPC-12 or EAN-13
    if (maxLength === 13 && gtin.length === 12) {
      const paddedGtin = gtin.padStart(14, '0');
      const calculatedCheckDigit = calculateCheckDigit(paddedGtin.slice(0, 13));
      const providedCheckDigit = parseInt(gtin.slice(-1));

      if (calculatedCheckDigit !== providedCheckDigit) {
        return {
          isValid: false,
          errorMessage: `Invalid check digit. Expected ${calculatedCheckDigit}, got ${providedCheckDigit}.`
        };
      }
      return { isValid: true, errorMessage: null };
    }
    
    // Check length constraints based on maxLength prop
    if (gtin.length < maxLength) {
      if (maxLength === 13) {
        return {
          isValid: false,
          errorMessage: 'UPC/EAN must be 12 or 13 digits.'
        };
      }
      const formatNames = {
        12: 'UPC',
        13: 'EAN', 
        14: 'GTIN'
      };
      const formatName = formatNames[maxLength] || 'identifier';
      return { 
        isValid: false, 
        errorMessage: `${formatName} must be ${maxLength} digits.` 
      };
    }
    
    if (gtin.length > maxLength) {
      if (maxLength === 13) {
        return {
          isValid: false,
          errorMessage: 'UPC/EAN cannot exceed 13 digits.'
        };
      }
      const formatNames = {
        12: 'UPC',
        13: 'EAN',
        14: 'GTIN'
      };
      const formatName = formatNames[maxLength] || 'identifier';
      return { 
        isValid: false, 
        errorMessage: `${formatName} cannot exceed ${maxLength} digits.` 
      };
    }
    
    // Validate check digit for the configured length
    if (maxLength === 12 || maxLength === 13 || maxLength === 14) {
      // Pad the GTIN to 14 digits with leading zeros for validation
      const paddedGtin = gtin.padStart(14, '0');
      const calculatedCheckDigit = calculateCheckDigit(paddedGtin.slice(0, 13));
      const providedCheckDigit = parseInt(gtin.slice(-1));
      
      if (calculatedCheckDigit !== providedCheckDigit) {
        return { 
          isValid: false, 
          errorMessage: `Invalid check digit. Expected ${calculatedCheckDigit}, got ${providedCheckDigit}.` 
        };
      }
    }
    
    return { isValid: true, errorMessage: null };
  };

  /**
   * Check for GTIN conflicts with existing products and casepacks
   */
  const checkGTINConflicts = (gtin) => {
    if (!gtin || gtin === '') return { hasConflict: false, conflictingProduct: null };
    const normalizedInput = normalizeGTINForStorage(gtin);

    // First, check if this GTIN conflicts with the current product's original GTIN
    // If it's the same, there's no conflict (user hasn't changed it)
    if (originalGtin) {
      const normalizedOriginalGtin = normalizeGTINForStorage(originalGtin);
      if (normalizedOriginalGtin === normalizedInput) {
        return { hasConflict: false, conflictingProduct: null }; // No change, no conflict
      }
    }

    // Check against existing product and casepack GTINs
    for (const product of existingProducts) {
      if (excludeProductId && product.id === excludeProductId) {
        continue;
      }

      const productLabel =
        product.product_name ||
        product.name ||
        product.customer_sku ||
        'another product';

      if (product.ean_upc && normalizeGTINForStorage(product.ean_upc) === normalizedInput) {
        return {
          hasConflict: true,
          conflictingProduct: productLabel
        };
      }

      if (product.casepacks) {
        for (const casepack of product.casepacks) {
          if (excludeCasepackId && casepack.id === excludeCasepackId) {
            continue;
          }

          if (casepack.gtin && normalizeGTINForStorage(casepack.gtin) === normalizedInput) {
            return {
              hasConflict: true,
              conflictingProduct:
                casepack.name || `${productLabel} - Case of ${casepack.units_per_casepack}`
            };
          }
        }
      }
    }
    
    // Check against other products being edited in bulk edit mode
    // Only consider products that have actually changed their GTIN
    if (isBulkEditing) {
      for (const id of Object.keys(bulkEditData)) {
        if (id !== excludeProductId && bulkEditData[id]?.gtin) {
          const otherProduct = existingProducts.find(p => p.id === id);
          const otherOriginalGtin = otherProduct?.ean_upc ? normalizeGTINForStorage(otherProduct.ean_upc) : null;
          const otherNewGtin = normalizeGTINForStorage(bulkEditData[id].ean_upc);
          
          // Only check for conflicts if the other product's GTIN has actually changed
          if (otherNewGtin !== otherOriginalGtin && otherNewGtin === normalizedInput) {
            return { 
              hasConflict: true, 
              conflictingProduct:
                otherProduct?.product_name ||
                otherProduct?.name ||
                'another product'
            };
          }
        }
      }
    }
    
    return { 
      hasConflict: false,
      conflictingProduct: null
    };
  };

  /**
   * Comprehensive GTIN validation
   */
  const validateGTIN = (gtin) => {
    // Step 1: Check if GTIN is empty (empty GTINs are valid)
    if (!gtin || gtin === '') {
      return { 
        isValid: true, 
        errorMessage: null, 
        hasConflict: false, 
        conflictingProduct: null 
      };
    }
    
    // Step 2: Validate GTIN format and check digit
    const formatValidation = validateGTINFormat(gtin);
    
    // If format validation failed, return early (no need to check conflicts)
    if (!formatValidation.isValid) {
      return {
        isValid: false,
        errorMessage: formatValidation.errorMessage,
        hasConflict: false,
        conflictingProduct: null
      };
    }
    
    // Step 3: Check for conflicts (only if format is valid)
    const conflictInfo = checkGTINConflicts(gtin);
    

    if (conflictInfo.hasConflict) {
      return {
        isValid: true, // Format is valid
        errorMessage: `UPC/GTIN already in use by ${conflictInfo.conflictingProduct}`,
        hasConflict: conflictInfo.hasConflict,
        conflictingProduct: conflictInfo.conflictingProduct
      };
    }
    return {
      isValid: true, // Format is valid
      errorMessage: null,
      hasConflict: false,
      conflictingProduct: null
    };
  };

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const newValue = e.target.value;
    
    // Only allow numbers and limit to 14 digits
    if (/^\d{0,14}$/.test(newValue)) {
      onChange(newValue);
    }
  };

  /**
   * Handle paste event
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const cleanedText = pastedText.replace(/\D/g, '').slice(0, 14);
    onChange(cleanedText);
  };

  /**
   * Handle blur event
   */
  const handleBlur = (e) => {
    setIsFocused(false);
    
    const currentValue = e.target.value;
    if (currentValue && currentValue.length > 0 && !alwaysShowPadding) {
      const cleaned = cleanGTINValue(currentValue);
      if (cleaned !== currentValue) {
        onChange(cleaned);
      }
    }
    
    if (onBlur) {
      onBlur(e);
    }
  };

  /**
   * Handle focus event
   */
  const handleFocus = () => {
    setIsFocused(true);
  };

  // Get validation results
  const validation = validateGTIN(value);
  
  // Determine if there are any errors
  const hasError = !validation.isValid || validation.hasConflict;
  
  // Get error message
  const getErrorMessage = () => {
    if (!validation.isValid) {
      return validation.errorMessage;
    }
    if (validation.hasConflict) {
      return `Already in use by ${validation.conflictingProduct}`;
    }
    return null;
  };

  const errorMessage = getErrorMessage();
  


            // Determine border and background colors
          const getBorderClasses = () => {
            if (borderless) {
              return hasError ? 'bg-red-50 border-red-300' : '';
            }
            if (hasError) {
              return 'border-red-500 bg-red-50';
            }
            return 'border-gray-300';
          };

  // Determine error message positioning
  const getErrorPositionClasses = () => {
    switch (errorPosition) {
      case 'right':
        return 'left-full ml-2';
      case 'bottom':
        return 'top-full mt-1 left-0';
      case 'left':
      default:
        return 'mt-1 left-0';
    }
  };

  const getPlaceholder = () => {
    if (placeholder) {
      return placeholder;
    }
    const formatNames = {
      12: 'UPC (12 digits)',
      13: 'EAN (13 digits)',
      14: 'GTIN (14 digits)'
    };
    return formatNames[maxLength] || 'Enter GTIN/UPC';
  };

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onPaste={handlePaste}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={getPlaceholder()}
        maxLength={maxLength}
        pattern="[0-9]*"
        inputMode="numeric"
        disabled={disabled}
        autoComplete={autoComplete}
                        className={`
                  text-sm h-8 w-full
                  ${borderless ? 'px-0 py-0 border border-gray-200 focus:border-green-400 focus:ring-1 focus:ring-green-400' : 'px-3 py-2 border rounded focus:ring-2 focus:ring-green-500'}
                  focus:outline-none text-gray-900
                  ${textAlign === 'center' ? 'text-center' : textAlign === 'right' ? 'text-right' : 'text-left'}
                  ${getBorderClasses()}
                  ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
                  ${className}
                `}
      />
      
      {/* Error Message */}
      {errorMessage && (
        <div 
          className={`
            absolute z-50 bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-700 shadow-lg
            top-full mt-1 left-0
          `}
          style={{ maxWidth: maxWidth }}
        >
          <div className="font-medium mb-1">Invalid GTIN/EAN/UPC</div>
          <div className="break-words whitespace-normal">
            {errorMessage}
          </div>
        </div>
      )}
    </div>
  );
};

export default GTINInput;
